/**
 * Reading what a dispatched data source answered.
 *
 * The endpoint's response is `additionalProperties` in the schema -- untyped, for
 * the same reason a row is: the shape depends on which service answered. There
 * are two, and this is the one place either is narrowed.
 *
 * The aggregation's own type, and the field it ran over, both travel *with the
 * data source* rather than with the answer, so nothing here can read a result
 * without the data source that produced it.
 */
import type { ChartDataSource, Field, SelectOption, SummaryDataSource } from '@/api/types'
import {
  aggregationText,
  isAggregationTypeName,
  type AggregationTypeName,
} from '../database/aggregations'

/** One bar, slice or point: a category and the number aggregated within it. */
export interface ChartPoint {
  /** The group-by value as the reader sees it. */
  readonly label: string
  /** The aggregation over that group, or null where it could not be computed. */
  readonly value: number | null
}

/**
 * The three labels a group-by key can need that are not the key itself.
 *
 * Passed in rather than translated here, which is what keeps every function in
 * this module a pure string transform with no translator threaded through it.
 */
export interface LabelWords {
  readonly empty: string
  readonly yes: string
  readonly no: string
}

/**
 * The serialised field a data source carries alongside its own configuration.
 *
 * `context_data` is `object` in the schema. It holds `field` for a summary and
 * `group_by_field` plus `aggregation_field` for a chart -- each a full field
 * serialisation, which is what lets a duration be summed and shown as a
 * duration.
 */
interface SummaryContext {
  field?: Field
}

interface ChartContext {
  group_by_field?: Field
  aggregation_field?: Field
}

function aggregationOf(type: string | undefined): AggregationTypeName | null {
  return isAggregationTypeName(type) ? type : null
}

/**
 * The `result` key of a dispatch response, or nothing when it is not there.
 *
 * No guard on the response being an object: reading a missing key off a string
 * or a number is `undefined` in any case, and the mutation pass could not tell
 * the guard from its absence because there is nothing to tell.
 */
function readResult(result: unknown): unknown {
  return (result as { result?: unknown } | null | undefined)?.result ?? null
}

/**
 * A summary widget's number, written the way its column writes it.
 *
 * Answers the empty string for a data source not yet configured, which is the
 * same answer as an aggregation over no rows -- and correctly so. A widget with
 * no field chosen has nothing to show, and the badge beside it says why.
 */
export function summaryText(source: SummaryDataSource, result: unknown): string {
  const type = aggregationOf(source.aggregation_type)
  const { field } = source.context_data as SummaryContext
  if (type === null || field === undefined) return ''

  return aggregationText(type, field, readResult(result))
}

/**
 * A chart widget's series, in the order the server grouped them.
 *
 * Not sorted here. The backend orders by the group-by field's own ordering, so
 * a chart of months runs January to December rather than alphabetically, and
 * re-sorting by value would throw that away.
 */
export function chartSeries(
  source: ChartDataSource,
  result: unknown,
  words: LabelWords,
): ChartPoint[] {
  const rows = readResult(result)
  if (!Array.isArray(rows)) return []

  const { group_by_field: groupBy } = source.context_data as ChartContext
  return rows.map((row: unknown) => {
    const { label, value } = (row ?? {}) as { label?: unknown; value?: unknown }
    return {
      label: categoryLabel(groupBy, label, words),
      value: typeof value === 'number' ? value : null,
    }
  })
}

/**
 * A chart value as text, for a tooltip.
 *
 * The axis shows the raw number, because an axis has to be evenly spaced; the
 * tooltip shows what the number *is* -- a sum of durations as a duration, a
 * percentage with its sign.
 */
export function chartValueText(source: ChartDataSource, value: number | null): string {
  const type = aggregationOf(source.aggregation_type)
  const { aggregation_field: field } = source.context_data as ChartContext
  if (type === null || field === undefined) return value === null ? '' : String(value)

  return aggregationText(type, field, value)
}

/**
 * A group-by value as the reader sees it.
 *
 * Not `valueText`: the label is the value the database grouped by, which for a
 * select field is the option's *id* rather than the option -- so the cell
 * rendering would be handed a number where it expects an object. The cases
 * below are the only shapes a group-by key comes in.
 */
export function categoryLabel(
  field: Field | undefined,
  label: unknown,
  words: LabelWords,
): string {
  if (label === null || label === undefined || label === '') return words.empty
  if (typeof label === 'boolean') return label ? words.yes : words.no

  const named = selectOptionNamed(field, label)
  if (named !== null) return named

  // The schema says a label is a string, a number, a boolean or null, and the
  // three other cases are above. Anything else is a shape nothing produces, and
  // rendering it would be `[object Object]` on the axis.
  return typeof label === 'string' || typeof label === 'number' ? String(label) : ''
}

function selectOptionNamed(field: Field | undefined, label: unknown): string | null {
  if (field === undefined) return null
  if (field.type !== 'single_select' && field.type !== 'multiple_select') return null

  const options: SelectOption[] | undefined = field.select_options
  return options?.find((one) => one.id === label)?.value ?? null
}
